import type { Dispatch, MutableRefObject, SetStateAction } from 'react'
import { useCompleteAssessment, useSubmitAssessmentAnswer } from '@/api/enrollment-api'
import type { EnglishTestQuestionDto } from '@/api/generated/models'
import { useEnrollmentStore } from '@/stores'
import { ENGLISH_TEST_MESSAGES, ENROLLMENT_COMMON_MESSAGES } from '../../constants/enrollmentContent'
import type { EnglishTestModalProps, EnglishTestAnswerReview, QuestionAnswerValue } from './types'
import {
  buildCompletedAssessmentReviewAnswers,
  buildMultipleChoiceSubmittedAnswer,
  buildTextSubmittedAnswer,
  getSubmissionErrorMessage
} from './answerReview'
import { byDisplayOrder, isTextEntryQuestion } from './questionUtils'

type UseEnglishTestSubmissionArgs = {
  assessmentId: number | null
  answers: QuestionAnswerValue[]
  defaultTest: EnglishTestModalProps['defaultTest']
  groupStartIndex: number
  onComplete: EnglishTestModalProps['onComplete']
  questions: EnglishTestQuestionDto[]
  setSubmissionError: (error: string | null) => void
  setSubmittedAnswers: Dispatch<SetStateAction<Record<number, EnglishTestAnswerReview>>>
  submittedAnswers: Record<number, EnglishTestAnswerReview>
  submittedTextAnswersRef: MutableRefObject<Record<number, string>>
  visibleQuestions: EnglishTestQuestionDto[]
}

export function useEnglishTestSubmission({
  assessmentId,
  answers,
  defaultTest,
  groupStartIndex,
  onComplete,
  questions,
  setSubmissionError,
  setSubmittedAnswers,
  submittedAnswers,
  submittedTextAnswersRef,
  visibleQuestions
}: UseEnglishTestSubmissionArgs) {
  const sessionToken = useEnrollmentStore(store => store.sessionToken)?.trim() ?? ''
  const submitAssessmentAnswerMutation = useSubmitAssessmentAnswer()
  const completeAssessmentMutation = useCompleteAssessment()

  const submitMultipleChoiceAnswer = async (
    question: EnglishTestQuestionDto,
    optionIndex: number
  ) => {
    if (!assessmentId) {
      throw new Error(ENGLISH_TEST_MESSAGES.assessmentMissing)
    }

    if (!sessionToken) {
      throw new Error(ENROLLMENT_COMMON_MESSAGES.sessionMissing)
    }

    const sortedAnswers = [...(question.answers ?? [])].sort(byDisplayOrder)
    const answer = sortedAnswers[optionIndex]

    if (!question.id || !answer?.id) {
      throw new Error(ENGLISH_TEST_MESSAGES.missingAnswerData)
    }

    const response = await submitAssessmentAnswerMutation.mutateAsync({
      sessionToken,
      assessmentId,
      data: {
        assessmentId,
        questionId: question.id,
        questionText: question.questionText,
        selectedAnswerId: answer.id,
        selectedAnswerText: answer.answerText
      }
    })

    setSubmittedAnswers(current => ({
      ...current,
      [question.id!]: buildMultipleChoiceSubmittedAnswer(question, optionIndex, response)
    }))
  }

  const submitTextAnswer = async (question: EnglishTestQuestionDto, value: string) => {
    if (!assessmentId) {
      throw new Error(ENGLISH_TEST_MESSAGES.assessmentMissing)
    }

    if (!sessionToken) {
      throw new Error(ENROLLMENT_COMMON_MESSAGES.sessionMissing)
    }

    if (!question.id) {
      throw new Error(ENGLISH_TEST_MESSAGES.missingAnswerData)
    }

    const textAnswer = value.trim()

    if (!textAnswer) {
      return
    }

    if (submittedTextAnswersRef.current[question.id] === textAnswer) {
      return
    }

    submittedTextAnswersRef.current[question.id] = textAnswer

    let response: Awaited<ReturnType<typeof submitAssessmentAnswerMutation.mutateAsync>>
    try {
      response = await submitAssessmentAnswerMutation.mutateAsync({
        sessionToken,
        assessmentId,
        data: {
          assessmentId,
          questionId: question.id,
          questionText: question.questionText,
          selectedAnswerText: textAnswer,
          textAnswer
        }
      })
    } catch (error) {
      if (submittedTextAnswersRef.current[question.id] === textAnswer) {
        delete submittedTextAnswersRef.current[question.id]
      }
      throw error
    }

    setSubmittedAnswers(current => ({
      ...current,
      [question.id!]: buildTextSubmittedAnswer(question, textAnswer, response)
    }))
  }

  const flushVisibleTextAnswers = async () => {
    const pendingTextAnswers = visibleQuestions.flatMap((question, visibleIndex) => {
      if (!isTextEntryQuestion(question)) {
        return []
      }

      const questionIndex = groupStartIndex + visibleIndex
      const selectedAnswer = answers[questionIndex]

      if (selectedAnswer?.type !== 'text') {
        return []
      }

      const textAnswer = selectedAnswer.value.trim()

      if (!textAnswer) {
        return []
      }

      return [{ question, value: textAnswer }]
    })

    for (const pendingAnswer of pendingTextAnswers) {
      await submitTextAnswer(pendingAnswer.question, pendingAnswer.value)
    }
  }

  const handleCompleteTest = async () => {
    if (!assessmentId) {
      setSubmissionError(ENGLISH_TEST_MESSAGES.assessmentMissing)
      return
    }

    if (!sessionToken) {
      setSubmissionError(ENROLLMENT_COMMON_MESSAGES.sessionMissing)
      return
    }

    setSubmissionError(null)

    try {
      await flushVisibleTextAnswers()

      const completedAssessment = await completeAssessmentMutation.mutateAsync({ sessionToken, assessmentId })
      onComplete({
        assessmentId: completedAssessment.id ?? assessmentId,
        status: completedAssessment.status ?? null,
        passed: completedAssessment.passed === true,
        score: Number(completedAssessment.score ?? 0),
        passingScore: Number(completedAssessment.passingScore ?? defaultTest?.passingScore ?? 0),
        answers: buildCompletedAssessmentReviewAnswers(
          questions,
          submittedAnswers,
          completedAssessment.answers
        )
      })
    } catch (error) {
      setSubmissionError(
        error instanceof Error ? error.message : ENGLISH_TEST_MESSAGES.testSubmitFailed
      )
    }
  }

  const handleSubmissionError = (error: unknown) => {
    setSubmissionError(getSubmissionErrorMessage(error))
  }

  return {
    handleCompleteTest,
    handleSubmissionError,
    isSubmitting:
      submitAssessmentAnswerMutation.isPending || completeAssessmentMutation.isPending,
    submitMultipleChoiceAnswer,
    submitTextAnswer,
    flushVisibleTextAnswers
  }
}
